import { type ConnectionPolicyReason, type NativeToolEffect, floorForEffect } from '@parcel/tools';
import type { SpecToolDecision } from '@parcel/types';
import { ApiError, absoluteApiUrl, apiFetch } from './api.js';
import type { ConnectionScopingMode } from './spec-connections.js';
import { DECISION_STRICTNESS } from './spec-tools.js';

/** Typed client for one connection's curated tool policy; wire shapes mirror
    apps/api/src/routes/spec-connection-tools.ts, the authority for the mapping below. */

// -- Client types --

// Mirrors @parcel/db's SpecConnectionCallOutcome; kept local so this client adds no
// server-only dependency (same reasoning as @parcel/tools' own SpecConnectionStatus mirror).
export type ConnectionCallOutcome = 'succeeded' | 'failed' | 'unknown';

export interface ConnectionToolPolicyRow {
  toolKey: string;
  providerSlug: string;
  description: string;
  effect: NativeToolEffect;
  /** True while no owner or admin has classified this custom tool at its current fingerprint. */
  unreviewed: boolean;
  /** Hash of the tool's current input schema; null when the server captured none. */
  inputSchemaFingerprint: string | null;
  version: string;
  registryFloor: SpecToolDecision;
  defaultPolicy: SpecToolDecision;
  /** What actually governs a call right now: floor, connection, and member combined. */
  decision: SpecToolDecision;
  reason: ConnectionPolicyReason;
  connectionDecision: SpecToolDecision | null;
  memberDecision: SpecToolDecision | null;
  memberCanAlwaysAllow?: boolean;
  revision: number;
  updatedAt: string | null;
  updatedBy: string | null;
  /** From this connection's own receipts; null when this tool has never been called (or not
      recently enough to still be in the server's recency window). */
  lastOutcome: ConnectionCallOutcome | null;
  lastOutcomeAt: string | null;
}

export interface ConnectionToolsSettings {
  connectionId: string;
  catalogKey: string;
  scopingMode: ConnectionScopingMode;
  role: 'owner' | 'admin' | 'member';
  canManage: boolean;
  tools: ConnectionToolPolicyRow[];
  missingTools: MissingConnectionTool[];
}

/** A classified tool the server stopped offering: kept for its review, out of the catalog. */
export interface MissingConnectionTool {
  toolKey: string;
  effect: NativeToolEffect | null;
  missingSince: string | null;
}

// -- Wire shapes (apps/api/src/routes/spec-connection-tools.ts) --

interface ConnectionToolWire {
  toolKey: string;
  providerSlug: string;
  description: string;
  effect: string;
  unreviewed?: boolean;
  inputSchemaFingerprint?: string | null;
  version: string;
  registryFloor: string;
  defaultPolicy: string;
  decision: string;
  reason: string;
  connectionDecision: string | null;
  memberDecision: string | null;
  memberCanAlwaysAllow?: boolean;
  revision: number;
  updatedAt: string | null;
  updatedBy: string | null;
  lastOutcome: string | null;
  lastOutcomeAt: string | null;
}

interface ConnectionToolsListWireResponse {
  connectionId: string;
  catalogKey: string;
  scopingMode: string;
  role: string;
  canManage: boolean;
  tools: ConnectionToolWire[];
  missingTools?: MissingConnectionToolWire[];
}

interface MissingConnectionToolWire {
  toolKey: string;
  effect: string | null;
  missingSince: string | null;
}

// Named field by field, never spread: a poisoned wire row must not carry an unexpected
// credential-shaped field (e.g. accessToken) straight to the client.
function toConnectionToolPolicyRow(wire: ConnectionToolWire): ConnectionToolPolicyRow {
  return {
    toolKey: wire.toolKey,
    providerSlug: wire.providerSlug,
    description: wire.description,
    effect: wire.effect as NativeToolEffect,
    unreviewed: wire.unreviewed === true,
    inputSchemaFingerprint: wire.inputSchemaFingerprint ?? null,
    version: wire.version,
    registryFloor: wire.registryFloor as SpecToolDecision,
    defaultPolicy: wire.defaultPolicy as SpecToolDecision,
    decision: wire.decision as SpecToolDecision,
    reason: wire.reason as ConnectionPolicyReason,
    connectionDecision: wire.connectionDecision as SpecToolDecision | null,
    memberDecision: wire.memberDecision as SpecToolDecision | null,
    memberCanAlwaysAllow: wire.memberCanAlwaysAllow === true,
    revision: wire.revision,
    updatedAt: wire.updatedAt,
    updatedBy: wire.updatedBy,
    lastOutcome: wire.lastOutcome as ConnectionCallOutcome | null,
    lastOutcomeAt: wire.lastOutcomeAt,
  };
}

// -- Errors --

const CONNECTION_TOOL_POLICY_ERROR_CODES = [
  'revision_conflict',
  'below_registry_floor',
  'below_connection_policy',
  'unknown_tool_key',
  'fingerprint_changed',
  'forbidden',
] as const;

export type ConnectionToolPolicyErrorCode = (typeof CONNECTION_TOOL_POLICY_ERROR_CODES)[number];

const KNOWN_CONNECTION_POLICY_ERROR_CODES = new Set<string>(CONNECTION_TOOL_POLICY_ERROR_CODES);

/** A known write rejection (e.g. `revision_conflict`, `below_registry_floor`), thrown in
    place of a raw `ApiError` so a caller can branch without string-matching. */
export class ConnectionToolPolicyError extends Error {
  readonly code: ConnectionToolPolicyErrorCode;
  readonly status: number;

  constructor(status: number, code: ConnectionToolPolicyErrorCode, message: string) {
    super(message);
    this.name = 'ConnectionToolPolicyError';
    this.code = code;
    this.status = status;
  }
}

function toConnectionToolPolicyError(err: unknown): unknown {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object') return err;
  const body = err.body as { error?: { code?: unknown; message?: unknown } };
  const code = body.error?.code;
  if (typeof code !== 'string' || !KNOWN_CONNECTION_POLICY_ERROR_CODES.has(code)) return err;
  const message = body.error?.message;
  return new ConnectionToolPolicyError(
    err.status,
    code as ConnectionToolPolicyErrorCode,
    typeof message === 'string' ? message : err.message,
  );
}

// -- Reads and writes --

export async function listConnectionTools(
  workspaceId: string,
  connectionId: string,
): Promise<ConnectionToolsSettings> {
  const wire = await apiFetch<ConnectionToolsListWireResponse>(
    `/workspaces/${workspaceId}/spec/connections/${connectionId}/tools`,
  );
  return {
    connectionId: wire.connectionId,
    catalogKey: wire.catalogKey,
    scopingMode: wire.scopingMode as ConnectionScopingMode,
    role: wire.role as ConnectionToolsSettings['role'],
    canManage: wire.canManage,
    tools: wire.tools.map(toConnectionToolPolicyRow),
    missingTools: (wire.missingTools ?? []).map((missing) => ({
      toolKey: missing.toolKey,
      effect: (missing.effect as NativeToolEffect | null) ?? null,
      missingSince: missing.missingSince ?? null,
    })),
  };
}

/** Owner/admin only. `expectedRevision` is the row's `revision` from the last list/write. */
export async function updateConnectionToolPolicy(
  workspaceId: string,
  connectionId: string,
  toolKey: string,
  decision: SpecToolDecision,
  expectedRevision: number,
): Promise<ConnectionToolPolicyRow> {
  try {
    const wire = await apiFetch<ConnectionToolWire>(
      `/workspaces/${workspaceId}/spec/connections/${connectionId}/tools/${encodeURIComponent(toolKey)}/policy`,
      { method: 'PATCH', body: JSON.stringify({ decision, expectedRevision }) },
    );
    return toConnectionToolPolicyRow(wire);
  } catch (err) {
    throw toConnectionToolPolicyError(err);
  }
}

/** Any member. `decision: null` clears the override and follows the connection again. */
export async function updateMyConnectionToolPolicy(
  workspaceId: string,
  connectionId: string,
  toolKey: string,
  decision: SpecToolDecision | null,
): Promise<ConnectionToolPolicyRow> {
  try {
    const wire = await apiFetch<ConnectionToolWire>(
      `/workspaces/${workspaceId}/spec/connections/${connectionId}/tools/${encodeURIComponent(toolKey)}/me`,
      { method: 'PATCH', body: JSON.stringify({ decision }) },
    );
    return toConnectionToolPolicyRow(wire);
  } catch (err) {
    throw toConnectionToolPolicyError(err);
  }
}

/** What one discovery run found, for the counts a refresh reports back. */
export interface ConnectionToolRefreshCounts {
  added: number;
  updated: number;
  removed: number;
  total: number;
}

interface ConnectionToolRefreshWire {
  connectionId: string;
  added: number;
  updated: number;
  removed: number;
  total: number;
}

/**
 * Owner/admin only. Records what one custom tool does, bound to the fingerprint the caller was
 * shown: a refresh that landed since then is refused with `fingerprint_changed`.
 */
export async function classifyConnectionTool(
  workspaceId: string,
  connectionId: string,
  toolKey: string,
  effect: NativeToolEffect,
  expectedFingerprint: string | null,
): Promise<ConnectionToolPolicyRow> {
  try {
    const wire = await apiFetch<ConnectionToolWire>(
      `/workspaces/${workspaceId}/spec/connections/${connectionId}/tools/${encodeURIComponent(toolKey)}/classify`,
      {
        method: 'POST',
        body: JSON.stringify({
          effect,
          ...(expectedFingerprint ? { expectedFingerprint } : {}),
        }),
      },
    );
    return toConnectionToolPolicyRow(wire);
  } catch (err) {
    throw toConnectionToolPolicyError(err);
  }
}

/** Owner/admin only. Re-reads what a custom server advertises and reports what moved. */
export async function refreshConnectionTools(
  workspaceId: string,
  connectionId: string,
): Promise<ConnectionToolRefreshCounts> {
  const wire = await apiFetch<ConnectionToolRefreshWire>(
    `/workspaces/${workspaceId}/spec/connections/${connectionId}/tools/refresh`,
    { method: 'POST' },
  );
  return {
    added: wire.added,
    updated: wire.updated,
    removed: wire.removed,
    total: wire.total,
  };
}

// -- Policy control floor --

function strictestDecision(a: SpecToolDecision, b: SpecToolDecision): SpecToolDecision {
  return DECISION_STRICTNESS[a] >= DECISION_STRICTNESS[b] ? a : b;
}

/** The floor a connection-layer edit must respect. Mirrors `connectionPolicyFloor` in
    apps/api/src/lib/spec/connections.ts -- the server's real enforcement; keep them in sync. */
export function connectionToolControlFloor(
  row: Pick<ConnectionToolPolicyRow, 'registryFloor' | 'defaultPolicy' | 'effect'>,
  scopingMode: ConnectionScopingMode,
  sharedPolicyFloor: SpecToolDecision,
): SpecToolDecision {
  const base = strictestDecision(
    strictestDecision(row.registryFloor, row.defaultPolicy),
    floorForEffect(row.effect),
  );
  return scopingMode === 'workspace' ? strictestDecision(base, sharedPolicyFloor) : base;
}

// -- Last outcome and audit link --

const LAST_OUTCOME_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * Succeeded is absent on purpose: a tool that worked is the expected case, and
 * saying so on every row it ever ran turns a settings list into a log.
 */
const LAST_OUTCOME_LABELS: Readonly<Record<ConnectionCallOutcome, string | null>> = {
  succeeded: null,
  failed: 'Failed',
  unknown: 'Outcome unknown',
};

/** Null when this tool has no call on record -- never a placeholder like "Never called": that
    reads as a fact this client cannot actually verify, only as "not in the recent window". */
export function describeLastConnectionCall(
  row: Pick<ConnectionToolPolicyRow, 'lastOutcome' | 'lastOutcomeAt'>,
): string | null {
  if (!row.lastOutcome || !row.lastOutcomeAt) return null;
  const label = LAST_OUTCOME_LABELS[row.lastOutcome];
  if (!label) return null;
  const date = LAST_OUTCOME_DATE_FORMAT.format(new Date(row.lastOutcomeAt));
  return `${label} on ${date}`;
}

/** `connection/<connectionId>/<toolKey>`: the canonical identity spec.action_audit_events
    stores its toolName as (apps/api/src/routes/spec-connection-actions.ts, canonicalConnectionToolKey). */
function canonicalConnectionToolKey(connectionId: string, toolKey: string): string {
  return `connection/${connectionId}/${toolKey}`;
}

/** Unused pending a web page to consume it (Ruling 41 removed the rendered link: the route
    needs a Bearer token a plain anchor click never sends). Kept for whoever builds that page. */
export function connectionToolAuditHref(
  workspaceId: string,
  connectionId: string,
  toolKey: string,
): string {
  const params = new URLSearchParams({
    source: 'connection',
    tool: canonicalConnectionToolKey(connectionId, toolKey),
  });
  return `${absoluteApiUrl(`/workspaces/${workspaceId}/spec/action-audit`)}?${params.toString()}`;
}

// -- Reading a connection tool call back into something a member recognizes --

/**
 * The model is offered `connection__<uuid>__<base64url(toolKey)>`, because an MCP
 * tool name must match `^[a-zA-Z0-9_-]{1,128}$` and a capability key like
 * `channel.search` does not. That alias is what lands in the transcript, so the
 * thread has to undo it rather than show the wire name.
 */
const CONNECTION_TOOL_ALIAS = /^connection__([0-9a-fA-F-]{36})__([A-Za-z0-9_-]+)$/;

export interface SpecConnectionToolRef {
  connectionId: string;
  /** The capability key, for example `channel.search`. */
  toolKey: string;
}

function decodeToolKey(encoded: string): string | null {
  const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  try {
    const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
    return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
  } catch {
    return null;
  }
}

/** Null for a native tool name, which is already the thing it says it is. */
export function parseConnectionToolAlias(toolName: string): SpecConnectionToolRef | null {
  const match = CONNECTION_TOOL_ALIAS.exec(toolName);
  if (!match) return null;
  const toolKey = decodeToolKey(match[2] ?? '');
  if (!toolKey) return null;
  return { connectionId: match[1] ?? '', toolKey };
}

function capitalizeFirstWord(word: string): string {
  return word.length === 0 ? word : `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
}

/**
 * "channel.search" -> "Channel search".
 *
 * Mirrors deriveSpecToolTitle for the dot-segmented capability keys curated
 * connection tools use instead of native tools' underscore names.
 */
export function connectorToolTitle(key: string): string {
  const words = key.split(/[._]+/).filter(Boolean);
  return words.map((word, index) => (index === 0 ? capitalizeFirstWord(word) : word)).join(' ');
}
